import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaUser } from 'react-icons/fa';
import { Link } from 'react-router-dom';

interface SubmitTemplateCardProps {
  onClickAction?: () => void;
  linkTo?: string;
  isSelected?: boolean;
  isDisabled?: boolean;
  className?: string;
}

const SubmitTemplateCard: React.FC<SubmitTemplateCardProps> = ({
  onClickAction,
  linkTo = 'contact',
  isSelected = false,
  isDisabled = false,
  className = '',
}) => {
  const { t } = useTranslation();

  const cardClasses = `tw-group tw-relative tw-w-full tw-h-full tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-overflow-hidden tw-transition-all tw-duration-300 tw-ease-in-out tw-shadow-sm tw-flex tw-flex-col hover:tw-shadow-xl hover:tw-border-blue-300 hover:-tw-translate-y-1 ${
    isDisabled ? 'tw-cursor-not-allowed tw-opacity-50' : 'tw-cursor-pointer'
  } ${isSelected ? 'tw-ring-2 tw-ring-blue-500 tw-border-blue-500 tw-shadow-lg' : ''} ${className}`;

  const content = (
    <>
      {/* Image Container with Plus Icon - 16:9 Aspect Ratio */}
      <div className="tw-relative tw-overflow-hidden">
        <div className="tw-relative tw-w-full tw-aspect-video tw-bg-gradient-to-br tw-from-blue-50 tw-to-indigo-100 tw-flex tw-items-center tw-justify-center">
          <div className="tw-w-20 tw-h-20 tw-bg-blue-500 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-shadow-lg tw-transition-transform tw-duration-300 group-hover:tw-scale-110">
            <svg width="32" height="32" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12.6641 2C7.15006 2 2.66406 6.486 2.66406 12C2.66406 17.514 7.15006 22 12.6641 22C18.1781 22 22.6641 17.514 22.6641 12C22.6641 6.486 18.1781 2 12.6641 2ZM17.6641 13H13.6641V17H11.6641V13H7.66406V11H11.6641V7H13.6641V11H17.6641V13Z"
                fill="white"
              />
            </svg>
          </div>

          {/* Gradient Overlay */}
          <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black/20 tw-via-transparent tw-to-transparent" />
        </div>
      </div>

      {/* Content Section */}
      <div className="tw-flex tw-flex-col tw-flex-1 tw-p-4">
        {/* Main Content */}
        <div className="tw-flex-1 tw-space-y-3">
          {/* Title */}
          <div className="tw-space-y-2">
            <h3 className="tw-font-semibold tw-text-gray-900 tw-text-base tw-line-clamp-2 tw-leading-tight tw-font-['Lexend'] tw-text-center">
              {t('TextScenario/key1')}
            </h3>

            {/* Empty Space for Better Layout */}
            <div className="tw-h-12 lg:tw-h-16">
              <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                <div className="tw-bg-gradient-to-r tw-from-purple-100 tw-to-blue-100 tw-text-blue-700 tw-px-2 tw-py-0.5 tw-rounded-md tw-text-xs tw-font-medium">
                  {t('TextScenario/submitTemplate')}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="tw-mt-auto tw-space-y-3">
          {/* Description Section */}
          <div className="tw-relative">
            <div className="tw-bg-gradient-to-r tw-from-purple-50 tw-to-indigo-50 tw-rounded-xl tw-p-3 tw-border tw-border-purple-200">
              <div className="tw-text-center">
                <p className="tw-text-sm tw-text-gray-600 tw-font-['Lexend']">{t('TextScenario/key2')}</p>
              </div>
            </div>
          </div>

          {/* Publisher Info - By You */}
          <div className="tw-pt-2 tw-border-t tw-border-gray-100">
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
              <div className="tw-w-5 tw-h-5 tw-bg-gradient-to-br tw-from-blue-500 tw-to-purple-600 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                <FaUser className="tw-text-white tw-text-xs" />
              </div>
              <span className="tw-text-gray-600 tw-text-sm tw-font-medium">{t('TemplateCard/by')} You</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // If we have a custom click action, use a div with onClick
  if (onClickAction) {
    return (
      <div
        className={cardClasses}
        onClick={!isDisabled ? onClickAction : undefined}
        onKeyDown={!isDisabled ? e => e.key === 'Enter' && onClickAction() : undefined}
        role="button"
        tabIndex={0}
        aria-disabled={isDisabled}
      >
        {content}
      </div>
    );
  }

  // Otherwise use Link component
  return (
    <div className={cardClasses}>
      {content}
      <Link to={linkTo} className="tw-absolute tw-inset-0 tw-z-10" aria-label={t('TextScenario/key1')} />
    </div>
  );
};

export default SubmitTemplateCard;
